import { createReducer, on } from '@ngrx/store';
import { initialLeadPipelineState, LeadPipelineState } from './lead-pipeline.state';
import {
  ActivityActions,
  CompaniesActions,
  DashboardActions,
  DetailActions,
  ExportsActions,
  LeadsActions,
  LifecycleActions,
  ListActions,
  NotificationsActions,
  PersonsActions,
  RunNowActions,
  RunsActions,
  WizardActions,
} from './lead-pipeline.actions';

function errorMessage(error: any): string {
  return error?.error?.message || error?.message || 'Something went wrong.';
}

/**
 * Copy of a slice's initial value, one level deep, so a reset slice never shares its
 * `meta` / `filters` objects with `initialLeadPipelineState`.
 */
function resetSlice<T extends Record<string, any>>(initial: T): T {
  const copy = { ...initial } as Record<string, any>;

  for (const key of Object.keys(copy)) {
    const value = copy[key];
    if (Array.isArray(value)) {
      copy[key] = [...value];
    } else if (value !== null && typeof value === 'object') {
      copy[key] = { ...value };
    }
  }

  return copy as T;
}

export const leadPipelineReducer = createReducer(
  initialLeadPipelineState,

  // ===========================================================================
  // List
  // ===========================================================================
  on(ListActions.loadRequested, (state): LeadPipelineState => ({
    ...state,
    list: { ...state.list, status: 'loading' },
  })),
  on(ListActions.loaded, (state, { items, meta, tabs }): LeadPipelineState => ({
    ...state,
    list: { ...state.list, items, meta, tabs, status: 'success' },
  })),
  on(ListActions.failed, (state): LeadPipelineState => ({
    ...state,
    list: { ...state.list, status: 'error' },
  })),
  on(ListActions.filtersUpdated, (state, { filters }): LeadPipelineState => ({
    ...state,
    list: { ...state.list, filters: { ...state.list.filters, ...filters } },
  })),
  on(ListActions.includeArchivedToggled, (state, { includeArchived }): LeadPipelineState => ({
    ...state,
    list: { ...state.list, includeArchived },
  })),

  // ===========================================================================
  // Wizard
  // ===========================================================================
  on(WizardActions.entered, (state, { mode, uuid }): LeadPipelineState => ({
    ...state,
    wizard: {
      ...initialLeadPipelineState.wizard,
      mode,
      editUuid: uuid ?? null,
      // sessionId is set by the Session Started action right after entry.
      sessionId: state.wizard.sessionId,
    },
  })),
  on(WizardActions.sessionStarted, (state, { sessionId }): LeadPipelineState => ({
    ...state,
    wizard: { ...state.wizard, sessionId },
  })),
  on(WizardActions.stepChanged, (state, { step }): LeadPipelineState => ({
    ...state,
    wizard: { ...state.wizard, step },
  })),
  on(WizardActions.filtersUpdated, (state, { filters }): LeadPipelineState => ({
    ...state,
    wizard: { ...state.wizard, filters },
  })),
  on(WizardActions.resetFilters, (state): LeadPipelineState => ({
    ...state,
    wizard: { ...state.wizard, filters: [], previewCounts: null, previewStatus: 'idle' },
  })),
  on(WizardActions.volumeSelected, (state, { leadsPerDay }): LeadPipelineState => ({
    ...state,
    wizard: { ...state.wizard, leadsPerDay },
  })),
  on(WizardActions.workspaceSelected, (state, { workspaceId }): LeadPipelineState => ({
    ...state,
    wizard: { ...state.wizard, workspaceId },
  })),
  on(WizardActions.titleChanged, (state, { title }): LeadPipelineState => ({
    ...state,
    wizard: { ...state.wizard, title },
  })),
  on(WizardActions.descriptionChanged, (state, { description }): LeadPipelineState => ({
    ...state,
    wizard: { ...state.wizard, description },
  })),
  // preview-counts — `requested` fires post distinct+debounce, so increment here.
  on(WizardActions.previewCountsRequested, (state): LeadPipelineState => ({
    ...state,
    wizard: {
      ...state.wizard,
      previewStatus: 'loading',
      previewError: null,
      previewRateLimitRetryAfter: null,
      uniquePayloadsSent: state.wizard.uniquePayloadsSent + 1,
    },
  })),
  // retry re-uses the same payload, so it does NOT increment uniquePayloadsSent.
  on(WizardActions.previewRetryRequested, (state): LeadPipelineState => ({
    ...state,
    wizard: {
      ...state.wizard,
      previewStatus: 'loading',
      previewError: null,
      previewRateLimitRetryAfter: null,
    },
  })),
  on(WizardActions.previewCountsLoaded, (state, { counts }): LeadPipelineState => ({
    ...state,
    wizard: { ...state.wizard, previewCounts: counts, previewStatus: 'success', previewError: null },
  })),
  on(WizardActions.previewRateLimited, (state, { retryAfter }): LeadPipelineState => ({
    ...state,
    wizard: { ...state.wizard, previewStatus: 'error', previewRateLimitRetryAfter: retryAfter },
  })),
  on(WizardActions.previewFailed, (state, { error }): LeadPipelineState => ({
    ...state,
    wizard: { ...state.wizard, previewStatus: 'error', previewError: errorMessage(error) },
  })),
  on(WizardActions.editLoaded, (state, { pipeline }): LeadPipelineState => ({
    ...state,
    wizard: {
      ...state.wizard,
      mode: 'edit',
      editUuid: pipeline.uuid,
      editStatus: pipeline.status,
      filters: pipeline.filters ?? [],
      leadsPerDay: (pipeline.leads_per_day === 5 || pipeline.leads_per_day === 10 ? pipeline.leads_per_day : null),
      workspaceId: String(pipeline.workspace_id),
      title: pipeline.title,
      description: pipeline.description ?? '',
    },
  })),
  on(WizardActions.launchRequested, WizardActions.updateRequested, (state): LeadPipelineState => ({
    ...state,
    wizard: { ...state.wizard, submitting: true, submitError: null },
  })),
  on(
    WizardActions.launchSucceeded,
    WizardActions.updateSucceeded,
    (state): LeadPipelineState => ({
      ...state,
      wizard: { ...state.wizard, submitting: false, submitError: null },
    }),
  ),
  on(
    WizardActions.launchFailed,
    WizardActions.updateFailed,
    (state, { error }): LeadPipelineState => ({
      ...state,
      wizard: { ...state.wizard, submitting: false, submitError: error?.error?.errors ?? null },
    }),
  ),
  on(WizardActions.cleared, (state): LeadPipelineState => ({
    ...state,
    wizard: { ...initialLeadPipelineState.wizard },
  })),

  // ===========================================================================
  // Detail
  // ===========================================================================
  on(DetailActions.requested, (state): LeadPipelineState => ({
    ...state,
    detail: { ...state.detail, status: 'loading' },
  })),
  on(DetailActions.loaded, (state, { pipeline }): LeadPipelineState => ({
    ...state,
    detail: { current: pipeline, status: 'success' },
  })),
  on(DetailActions.failed, (state): LeadPipelineState => ({
    ...state,
    detail: { ...state.detail, status: 'error' },
  })),
  // Every tab slice below is per-pipeline: its rows, filters and paging describe the
  // pipeline being left. Keeping any of it means the next pipeline opens showing the
  // previous one's data (and, for companies/persons, silently filtered by its search).
  // In-flight requests are cancelled on this same action in the effects, so a late
  // response cannot refill what is reset here.
  on(DetailActions.cleared, (state): LeadPipelineState => ({
    ...state,
    detail: { current: null, status: 'idle' },
    dashboard: resetSlice(initialLeadPipelineState.dashboard),
    companies: resetSlice(initialLeadPipelineState.companies),
    persons: resetSlice(initialLeadPipelineState.persons),
    leads: resetSlice(initialLeadPipelineState.leads),
    activity: resetSlice(initialLeadPipelineState.activity),
    runs: resetSlice(initialLeadPipelineState.runs),
    exports: resetSlice(initialLeadPipelineState.exports),
  })),

  // ===========================================================================
  // Lifecycle
  // ===========================================================================
  on(LifecycleActions.mutated, (state, { pipeline }): LeadPipelineState => {
    // Guard against a malformed/empty response body — a reducer throw would take
    // down the whole store, so never dereference an undefined pipeline.
    if (!pipeline?.uuid) {
      return state;
    }
    return {
      ...state,
      detail:
        state.detail.current?.uuid === pipeline.uuid
          ? { ...state.detail, current: pipeline }
          : state.detail,
      list: {
        ...state.list,
        items: state.list.items.map(p => (p.uuid === pipeline.uuid ? pipeline : p)),
      },
    };
  }),
  on(LifecycleActions.deleted, (state, { uuid }): LeadPipelineState => ({
    ...state,
    detail:
      state.detail.current?.uuid === uuid
        ? { current: null, status: 'idle' }
        : state.detail,
    list: {
      ...state.list,
      items: state.list.items.filter(p => p.uuid !== uuid),
    },
  })),

  // ===========================================================================
  // Dashboard
  // ===========================================================================
  on(DashboardActions.requested, (state): LeadPipelineState => ({
    ...state,
    dashboard: { ...state.dashboard, status: 'loading' },
  })),
  on(DashboardActions.loaded, (state, { tiles, chart }): LeadPipelineState => ({
    ...state,
    dashboard: { ...state.dashboard, tiles, chart, status: 'success' },
  })),
  on(DashboardActions.failed, (state): LeadPipelineState => ({
    ...state,
    dashboard: { ...state.dashboard, status: 'error' },
  })),
  on(DashboardActions.chartRangeChanged, (state, { range }): LeadPipelineState => ({
    ...state,
    dashboard: { ...state.dashboard, chartRange: range },
  })),

  // ===========================================================================
  // Companies
  // ===========================================================================
  on(CompaniesActions.requested, (state): LeadPipelineState => ({
    ...state,
    companies: { ...state.companies, status: 'loading' },
  })),
  on(CompaniesActions.loadMoreRequested, (state): LeadPipelineState => ({
    ...state,
    companies: { ...state.companies, status: 'loading' },
  })),
  on(CompaniesActions.loaded, (state, { items, meta, append }): LeadPipelineState => ({
    ...state,
    companies: {
      ...state.companies,
      items: append ? [...state.companies.items, ...items] : items,
      meta,
      status: 'success',
    },
  })),
  on(CompaniesActions.failed, (state): LeadPipelineState => ({
    ...state,
    companies: { ...state.companies, status: 'error' },
  })),
  on(CompaniesActions.filtersUpdated, (state, { filters }): LeadPipelineState => ({
    ...state,
    companies: { ...state.companies, filters: { ...state.companies.filters, ...filters } },
  })),

  // ===========================================================================
  // Persons
  // ===========================================================================
  on(PersonsActions.requested, (state): LeadPipelineState => ({
    ...state,
    persons: { ...state.persons, status: 'loading' },
  })),
  on(PersonsActions.loadMoreRequested, (state): LeadPipelineState => ({
    ...state,
    persons: { ...state.persons, status: 'loading' },
  })),
  on(PersonsActions.loaded, (state, { items, meta, append }): LeadPipelineState => ({
    ...state,
    persons: {
      ...state.persons,
      items: append ? [...state.persons.items, ...items] : items,
      meta,
      status: 'success',
    },
  })),
  on(PersonsActions.failed, (state): LeadPipelineState => ({
    ...state,
    persons: { ...state.persons, status: 'error' },
  })),
  on(PersonsActions.filtersUpdated, (state, { filters }): LeadPipelineState => ({
    ...state,
    persons: { ...state.persons, filters: { ...state.persons.filters, ...filters } },
  })),
  on(PersonsActions.unlockEmailRequested, (state, { personUuid }): LeadPipelineState => ({
    ...state,
    persons: { ...state.persons, unlockingIds: [...state.persons.unlockingIds, personUuid] },
  })),
  on(PersonsActions.emailUnlocked, (state, { result }): LeadPipelineState => ({
    ...state,
    persons: {
      ...state.persons,
      unlockingIds: state.persons.unlockingIds.filter(id => id !== result.person_uuid),
      items: state.persons.items.map(p =>
        p.uuid === result.person_uuid
          ? { ...p, email: result.person_email, email_status: result.email_status }
          : p,
      ),
    },
    // Keep an open lead drawer in sync when its person's email is unlocked.
    leads:
      state.leads.selectedLead && state.leads.selectedLead.person_uuid === result.person_uuid
        ? { ...state.leads, selectedLead: { ...state.leads.selectedLead, person_email: result.person_email } }
        : state.leads,
  })),
  on(PersonsActions.unlockEmailFailed, (state, { personUuid }): LeadPipelineState => ({
    ...state,
    persons: {
      ...state.persons,
      unlockingIds: state.persons.unlockingIds.filter(id => id !== personUuid),
    },
  })),

  // ===========================================================================
  // Qualified leads
  // ===========================================================================
  on(LeadsActions.requested, (state): LeadPipelineState => ({
    ...state,
    leads: { ...state.leads, status: 'loading' },
  })),
  on(LeadsActions.loadMoreRequested, (state): LeadPipelineState => ({
    ...state,
    leads: { ...state.leads, status: 'loading' },
  })),
  on(LeadsActions.loaded, (state, { items, meta, append }): LeadPipelineState => ({
    ...state,
    leads: {
      ...state.leads,
      items: append ? [...state.leads.items, ...items] : items,
      meta,
      status: 'success',
    },
  })),
  on(LeadsActions.failed, (state): LeadPipelineState => ({
    ...state,
    leads: { ...state.leads, status: 'error' },
  })),
  on(LeadsActions.tabChanged, (state, { tab }): LeadPipelineState => ({
    ...state,
    leads: { ...state.leads, filters: { ...state.leads.filters, tab } },
  })),
  on(LeadsActions.sortChanged, (state, { sort }): LeadPipelineState => ({
    ...state,
    leads: { ...state.leads, filters: { ...state.leads.filters, sort } },
  })),
  on(LeadsActions.detailRequested, (state): LeadPipelineState => ({
    ...state,
    leads: { ...state.leads, drawerStatus: 'loading' },
  })),
  on(LeadsActions.detailLoaded, (state, { lead }): LeadPipelineState => ({
    ...state,
    leads: { ...state.leads, selectedLead: lead, drawerStatus: 'success' },
  })),
  on(LeadsActions.detailFailed, (state): LeadPipelineState => ({
    ...state,
    leads: { ...state.leads, drawerStatus: 'error' },
  })),
  on(LeadsActions.drawerClosed, (state): LeadPipelineState => ({
    ...state,
    leads: { ...state.leads, selectedLead: null, drawerStatus: 'idle' },
  })),
  // Flip the card to "View Profile" immediately after a report is generated,
  // without waiting for a reload. Persisted server-side (is_enhanced /
  // enhanced_report_id), so it also survives a refresh via normalizeLead.
  on(LeadsActions.enhanced, (state, { leadUuid, reportId }): LeadPipelineState => ({
    ...state,
    leads: {
      ...state.leads,
      items: state.leads.items.map(lead =>
        lead.uuid === leadUuid
          ? { ...lead, is_enhanced: true, enhanced_report_id: reportId ?? lead.enhanced_report_id }
          : lead,
      ),
      selectedLead:
        state.leads.selectedLead?.uuid === leadUuid
          ? { ...state.leads.selectedLead, is_enhanced: true, enhanced_report_id: reportId ?? state.leads.selectedLead.enhanced_report_id }
          : state.leads.selectedLead,
    },
  })),
  on(LeadsActions.statusUpdated, LeadsActions.feedbackSubmitted, (state, { lead }): LeadPipelineState => ({
    ...state,
    leads: {
      ...state.leads,
      items: state.leads.items.map(l => (l.uuid === lead.uuid ? { ...l, ...lead } : l)),
      selectedLead:
        state.leads.selectedLead?.uuid === lead.uuid
          ? { ...state.leads.selectedLead, ...lead }
          : state.leads.selectedLead,
    },
  })),

  // ===========================================================================
  // Activity
  // ===========================================================================
  on(ActivityActions.requested, (state): LeadPipelineState => ({
    ...state,
    activity: { ...state.activity, status: 'loading' },
  })),
  on(ActivityActions.loadMoreRequested, (state): LeadPipelineState => ({
    ...state,
    activity: { ...state.activity, status: 'loading' },
  })),
  on(ActivityActions.loaded, (state, { items, meta, append }): LeadPipelineState => ({
    ...state,
    activity: {
      ...state.activity,
      items: append ? [...state.activity.items, ...items] : items,
      meta,
      status: 'success',
    },
  })),
  on(ActivityActions.failed, (state): LeadPipelineState => ({
    ...state,
    activity: { ...state.activity, status: 'error' },
  })),
  on(ActivityActions.filterChanged, (state, { filters }): LeadPipelineState => ({
    ...state,
    activity: { ...state.activity, filters: { ...state.activity.filters, ...filters } },
  })),

  // ===========================================================================
  // Runs
  // ===========================================================================
  on(RunsActions.requested, (state): LeadPipelineState => ({
    ...state,
    runs: { ...state.runs, status: 'loading' },
  })),
  on(RunsActions.loadMoreRequested, (state): LeadPipelineState => ({
    ...state,
    runs: { ...state.runs, status: 'loading' },
  })),
  on(RunsActions.loaded, (state, { items, meta, append }): LeadPipelineState => ({
    ...state,
    runs: {
      ...state.runs,
      items: append ? [...state.runs.items, ...items] : items,
      meta,
      status: 'success',
    },
  })),
  on(RunsActions.failed, (state): LeadPipelineState => ({
    ...state,
    runs: { ...state.runs, status: 'error' },
  })),
  on(RunsActions.detailLoaded, (state, { run }): LeadPipelineState => ({
    ...state,
    runs: { ...state.runs, currentRun: run },
  })),
  on(RunsActions.detailClosed, (state): LeadPipelineState => ({
    ...state,
    runs: { ...state.runs, currentRun: null },
  })),

  // ===========================================================================
  // Notifications
  // ===========================================================================
  on(NotificationsActions.requested, (state): LeadPipelineState => ({
    ...state,
    notifications: { ...state.notifications, status: 'loading' },
  })),
  on(NotificationsActions.loaded, (state, { items, unreadCount }): LeadPipelineState => ({
    ...state,
    notifications: { items, unreadCount, status: 'success' },
  })),
  on(NotificationsActions.failed, (state): LeadPipelineState => ({
    ...state,
    notifications: { ...state.notifications, status: 'error' },
  })),

  // ===========================================================================
  // Exports
  // ===========================================================================
  on(ExportsActions.requested, (state): LeadPipelineState => ({
    ...state,
    exports: { ...state.exports, status: 'loading' },
  })),
  on(ExportsActions.loaded, (state, { items }): LeadPipelineState => ({
    ...state,
    exports: { ...state.exports, items, status: 'success' },
  })),
  on(ExportsActions.failed, (state): LeadPipelineState => ({
    ...state,
    exports: { ...state.exports, status: 'error' },
  })),
  on(ExportsActions.createRequested, (state, { entity }): LeadPipelineState => ({
    ...state,
    exports: { ...state.exports, creating: { entity, status: 'loading' } },
  })),
  on(ExportsActions.createStarted, (state): LeadPipelineState => ({
    ...state,
    exports: { ...state.exports, creating: null },
  })),
  on(ExportsActions.createFailed, (state): LeadPipelineState => ({
    ...state,
    exports: { ...state.exports, creating: null },
  })),
  on(ExportsActions.exportUpdated, (state, { export: updated }): LeadPipelineState => {
    const exists = state.exports.items.some(e => e.uuid === updated.uuid);
    return {
      ...state,
      exports: {
        ...state.exports,
        items: exists
          ? state.exports.items.map(e => (e.uuid === updated.uuid ? updated : e))
          : [updated, ...state.exports.items],
      },
    };
  }),

  // ===========================================================================
  // Run-now
  // ===========================================================================
  on(RunNowActions.confirmModalOpened, (state): LeadPipelineState => ({
    ...state,
    runNow: { ...state.runNow, confirmModalOpen: true },
  })),
  on(RunNowActions.confirmModalClosed, (state): LeadPipelineState => ({
    ...state,
    runNow: { ...state.runNow, confirmModalOpen: false },
  })),
  on(RunNowActions.dispatched, RunNowActions.inFlight, (state, { uuid }): LeadPipelineState => ({
    ...state,
    runNow: {
      confirmModalOpen: false,
      inFlight: { ...state.runNow.inFlight, [uuid]: true },
    },
  })),
  on(RunNowActions.failed, (state): LeadPipelineState => ({
    ...state,
    runNow: { ...state.runNow, confirmModalOpen: false },
  })),
  on(RunNowActions.completed, (state, { uuid }): LeadPipelineState => ({
    ...state,
    runNow: { ...state.runNow, inFlight: { ...state.runNow.inFlight, [uuid]: false } },
  })),
);

export type { LeadPipelineState };
